
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, VModel, Watch } from 'vue-property-decorator';
import Datim from '@/components/shared/Datim.vue';
import AuditLogActionPill from '@/components/settings/auditLogs/AuditLogActionPill.vue';
import {
  AUDIT_LOG_ACTION_DELETE,
  AUDIT_LOG_ACTION_INSERT,
  IAuditLogEntry,
} from '@/services/auditLogService';

interface IAuditLogChange {
  field: string;
  before: string;
  after: string;
}

// Audited tables whose record has its own backoffice page.
const RECORD_ROUTES: { [table: string]: { name: string; param: string } } = {
  users: { name: 'user', param: 'userId' },
  service_providers: { name: 'serviceProvider', param: 'spId' },
};

@Component({
  name: 'AuditLogDetailDialog',
  components: { Datim, AuditLogActionPill },
})
export default class AuditLogDetailDialog extends Vue {
  @VModel({ type: Boolean })
  protected open!: boolean;

  @Prop({ default: null })
  protected entry!: IAuditLogEntry | null;

  protected tab: number = 0;

  @Watch('entry')
  onEntryChange() {
    this.tab = 0;
  }

  get recordRoute(): object | null {
    if (!this.entry || this.entry.record_id === null || !RECORD_ROUTES[this.entry.table_name]) {
      return null;
    }
    const route = RECORD_ROUTES[this.entry.table_name];

    return { name: route.name, params: { [route.param]: this.entry.record_id } };
  }

  get showsBefore(): boolean {
    return !!this.entry && this.entry.action !== AUDIT_LOG_ACTION_INSERT;
  }

  get showsAfter(): boolean {
    return !!this.entry && this.entry.action !== AUDIT_LOG_ACTION_DELETE;
  }

  /**
   * An update stores the full original row in old_data but only the dirty fields in new_data,
   * so the changes are the new_data keys. Inserts and deletes only carry one side.
   */
  get changes(): IAuditLogChange[] {
    if (!this.entry) {
      return [];
    }
    const before = this.entry.old_data || {};
    const after = this.entry.new_data || {};
    const fields = this.entry.action === AUDIT_LOG_ACTION_DELETE ? Object.keys(before) : Object.keys(after);

    return fields.map(field => ({
      field,
      before: this.display(before[field]),
      after: this.display(after[field]),
    }));
  }

  pretty(data: object | null): string {
    return data ? JSON.stringify(data, null, 2) : '';
  }

  private display(value: any): string {
    if (value === null || value === undefined || value === '') {
      return this.$t('auditLogs.empty') as string;
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    return String(value);
  }
}
